/**
 * Datenbankzeilen → OrgSettings und DunningPolicy. Die Spaltennamen sind verbindlich
 * (siehe Tabellen org_settings, dunning_policies, dunning_steps, dunning_base_rates).
 *
 * Fehlt ein Wert in der Agenturzeile (null/undefined), gilt der Plattform-Standard mit
 * demselben Schlüssel aus `platform_defaults`. Fehlt auch der, wirft die Funktion
 * DomainError('SETTING_MISSING', key). Es gibt keinen stillen Rückfall auf feste Werte.
 *
 * Basispunkte: 2500 = 25 %. Alle Prozentwerte der Fachlogik sind Prozent, nie Basispunkte.
 */
import { isCurrency } from "./money.js";
import { isIsoDate } from "./periods.js";
import {
  DUNNING_COLLECTION,
  DUNNING_NONE,
  DomainError,
  type Currency,
  type DunningPolicy,
  type DunningStageConfig,
  type InvoiceNumberScheme,
  type Language,
  type MoneyLocale,
  type OrgSettings,
} from "./types.js";

type Nullable<T> = T | null | undefined;

export interface OrgSettingsRow {
  timezone?: Nullable<string>;
  locale?: Nullable<string>;
  currency?: Nullable<string>;
  currencies?: Nullable<string[]>;
  default_document_language?: Nullable<string>;
  /** Basispunkte, 2500 = 25 % */
  default_commission_bp?: Nullable<number>;
  payment_terms_days?: Nullable<number>;
  brand_payment_terms_days?: Nullable<number>;
  talent_payment_terms_days?: Nullable<number>;
  due_soon_days?: Nullable<number>;
  payment_check_cooldown_days?: Nullable<number>;
  still_overdue_recheck_days?: Nullable<number>;
  talent_answer_days?: Nullable<number>;
  min_line_description_chars?: Nullable<number>;
  invoice_date_workdays_only?: Nullable<boolean>;
  budget_deviation_percent?: Nullable<number>;
  expense_keywords?: Nullable<string[]>;
  signing_link_days?: Nullable<number>;
  /** jsonb {monthCheck: 'off'|'detect'|'strict', template?: string} (strict seit 0105) */
  invoice_number_scheme?: Nullable<unknown>;
}

export interface DunningPolicyRow {
  interest_enabled?: Nullable<boolean>;
  /** Aufschlag in Basispunkten, 900 = 9 Prozentpunkte */
  surcharge_bp?: Nullable<number>;
  fee_mode?: Nullable<string>;
  /** einmalige Pauschale in Cent (fällt bei der ersten Nicht-Erinnerungsstufe an) */
  flat_fee_cents?: Nullable<number>;
  fee_currency?: Nullable<string>;
  /** Kosten auch bei der Mahnung im Auftrag des Creators (0161, Vorgabe an) */
  creator_claims_costs?: Nullable<boolean>;
  /** CH-2 (0271): „legal“ = Regel des Landes, „contract“ = vertraglich vereinbarter fester Satz (contract_rate_bp) */
  interest_rate_mode?: Nullable<string>;
  contract_rate_bp?: Nullable<number>;
  /** CH-2 (0271): „due“ bzw. „reminder“; leer = Vorgabe des Landes (Schweiz/Liechtenstein ab Mahnung) */
  interest_start?: Nullable<string>;
}

export interface DunningStepRow {
  step: number;
  key: string;
  days: number;
  day_type: "business" | "calendar" | string;
  fee_cents: number;
  interest: boolean;
  is_reminder: boolean;
  /** eigene Bezeichnung der Stufe (0109) */
  label?: string | null;
}

export interface DunningBaseRateRow {
  valid_from: string;
  /** Basispunkte, 152 = 1,52 % */
  rate_bp: number;
}

export interface OrgRows {
  org_settings: Nullable<OrgSettingsRow>;
  dunning_policy?: Nullable<DunningPolicyRow>;
  dunning_steps?: Nullable<DunningStepRow[]>;
  dunning_base_rates?: Nullable<DunningBaseRateRow[]>;
  /** Plattform-Standards mit denselben Schlüsseln wie die Spalten */
  platform_defaults: Record<string, unknown>;
}

/** OrgSettings der Fachlogik plus die Spalten, die dort (noch) kein Feld haben. */
export interface ResolvedOrgSettings extends OrgSettings {
  locale: MoneyLocale;
  currency: Currency;
  brandPaymentTermDays: number;
  talentPaymentTermDays: number;
}

export interface OrgConfig {
  settings: ResolvedOrgSettings;
  dunning: DunningPolicy;
}

const LOCALES: readonly string[] = ["de-DE", "de-AT", "de-CH", "en-GB"];
const LANGUAGES: readonly string[] = ["de", "en"];

export function bpToPercent(bp: number): number {
  if (!Number.isFinite(bp)) throw new DomainError("SETTING_INVALID", `Basispunkte: ${bp}`);
  return bp / 100;
}

function pick(row: Record<string, unknown>, defaults: Record<string, unknown>, key: string, derived?: unknown): unknown {
  const v = row[key];
  if (v !== null && v !== undefined) return v;
  const d = defaults[key];
  if (d !== null && d !== undefined) return d;
  // Abgeleiteter Wert aus einer anderen Pflichtangabe der Agentur, keine erfundene Konstante (R-21)
  if (derived !== null && derived !== undefined) return derived;
  throw new DomainError("SETTING_MISSING", key);
}

/** Schlüssel, die das System für „keine Stufe“ und „im Inkasso“ benutzt; als Mahnstufe verboten (R-22). */
const RESERVED_STAGE_KEYS: readonly string[] = [DUNNING_NONE, DUNNING_COLLECTION];

function invalid(key: string, value: unknown): never {
  throw new DomainError("SETTING_INVALID", `${key}: ${JSON.stringify(value)}`);
}

function num(v: unknown, key: string): number {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  if (!Number.isFinite(n)) invalid(key, v);
  return n;
}

function int(v: unknown, key: string): number {
  const n = num(v, key);
  if (!Number.isInteger(n)) invalid(key, v);
  return n;
}

function bool(v: unknown, key: string): boolean {
  if (typeof v === "boolean") return v;
  invalid(key, v);
}

function str(v: unknown, key: string): string {
  if (typeof v === "string" && v.trim() !== "") return v.trim();
  invalid(key, v);
}

function strList(v: unknown, key: string): string[] {
  if (Array.isArray(v) && v.every((x) => typeof x === "string")) return v as string[];
  invalid(key, v);
}

function currency(v: unknown, key: string): Currency {
  const s = str(v, key).toUpperCase();
  if (!isCurrency(s)) invalid(key, v);
  return s;
}

function oneOf<T extends string>(v: unknown, key: string, allowed: readonly string[]): T {
  const s = str(v, key);
  if (!allowed.includes(s)) invalid(key, v);
  return s as T;
}

function scheme(v: unknown, key: string): InvoiceNumberScheme {
  const raw = typeof v === "string" ? safeJson(v) : v;
  if (!raw || typeof raw !== "object") invalid(key, v);
  const o = raw as Record<string, unknown>;
  const monthCheck = oneOf<"off" | "detect" | "strict">(o.monthCheck, `${key}.monthCheck`, ["off", "detect", "strict"]);
  const out: InvoiceNumberScheme = { monthCheck };
  if (o.template !== undefined && o.template !== null) out.template = str(o.template, `${key}.template`);
  return out;
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/** Baut die Fachlogik-Konfiguration einer Agentur aus den Datenbankzeilen. */
export function orgSettingsFromRows(rows: OrgRows): OrgConfig {
  const defaults = rows.platform_defaults ?? {};
  const org = (rows.org_settings ?? {}) as Record<string, unknown>;
  const get = (key: string) => pick(org, defaults, key);

  const settings: ResolvedOrgSettings = {
    timeZone: str(get("timezone"), "timezone"),
    locale: oneOf<MoneyLocale>(get("locale"), "locale", LOCALES),
    currency: currency(get("currency"), "currency"),
    // Ohne eigene Liste ist nur die Hauptwährung der Agentur erlaubt (abgeleitet, keine erfundene Konstante)
    currencies: strList(pick(org, defaults, "currencies", [get("currency")]), "currencies").map((c) => currency(c, "currencies")),
    // Ohne eigene Angabe: Sprache der Locale der Agentur (de-AT → de, en-GB → en)
    ownOrgLanguage: oneOf<Language>(pick(org, defaults, "default_document_language", String(get("locale")).slice(0, 2)), "default_document_language", LANGUAGES),
    defaultCommissionPercent: bpToPercent(int(get("default_commission_bp"), "default_commission_bp")),
    defaultPaymentTermDays: int(get("payment_terms_days"), "payment_terms_days"),
    // Eigene Zahlungsziele für Marken und Talents sind optional; ohne Angabe gilt das allgemeine Zahlungsziel der Agentur.
    brandPaymentTermDays: int(pick(org, defaults, "brand_payment_terms_days", get("payment_terms_days")), "brand_payment_terms_days"),
    talentPaymentTermDays: int(pick(org, defaults, "talent_payment_terms_days", get("payment_terms_days")), "talent_payment_terms_days"),
    dueSoonDays: int(get("due_soon_days"), "due_soon_days"),
    paymentCheckCooldownDays: int(get("payment_check_cooldown_days"), "payment_check_cooldown_days"),
    stillOverdueRecheckDays: int(get("still_overdue_recheck_days"), "still_overdue_recheck_days"),
    talentAnswerDays: int(get("talent_answer_days"), "talent_answer_days"),
    minLineDescriptionChars: int(get("min_line_description_chars"), "min_line_description_chars"),
    invoiceDateWorkdaysOnly: bool(get("invoice_date_workdays_only"), "invoice_date_workdays_only"),
    budgetDeviationPercent: num(get("budget_deviation_percent"), "budget_deviation_percent"),
    expenseKeywords: strList(get("expense_keywords"), "expense_keywords"),
    signingLinkDays: int(get("signing_link_days"), "signing_link_days"),
    invoiceNumberScheme: scheme(get("invoice_number_scheme"), "invoice_number_scheme"),
  };

  const pol = (rows.dunning_policy ?? {}) as Record<string, unknown>;
  const getPol = (key: string) => pick(pol, defaults, key);
  const interestEnabled = bool(getPol("interest_enabled"), "interest_enabled");
  const feeMode = oneOf<"cumulative" | "highest">(getPol("fee_mode"), "fee_mode", ["cumulative", "highest"]);
  // Ohne Angabe: keine einmalige Pauschale; Gebühren in der Hauptwährung der Agentur.
  const flatFeeCents = int(pick(pol, defaults, "flat_fee_cents", 0), "flat_fee_cents");
  const feeCurrency = currency(pick(pol, defaults, "fee_currency", settings.currency), "fee_currency");
  const surchargePercentPoints = bpToPercent(int(getPol("surcharge_bp"), "surcharge_bp"));

  const stepRows = rows.dunning_steps && rows.dunning_steps.length > 0 ? rows.dunning_steps : defaults.dunning_steps;
  if (!Array.isArray(stepRows)) throw new DomainError("SETTING_MISSING", "dunning_steps");
  const stages: DunningStageConfig[] = [...(stepRows as DunningStepRow[])]
    .sort((a, b) => int(a.step, "dunning_steps.step") - int(b.step, "dunning_steps.step"))
    .map((s) => {
      const dayType = oneOf<"business" | "calendar">(s.day_type, "dunning_steps.day_type", ["business", "calendar"]);
      const key = str(s.key, "dunning_steps.key");
      if (RESERVED_STAGE_KEYS.includes(key)) invalid("dunning_steps.key", key);
      return {
        key,
        deadline: { days: int(s.days, "dunning_steps.days"), unit: dayType === "business" ? "working" : "calendar" },
        feeCents: int(s.fee_cents, "dunning_steps.fee_cents"),
        interest: interestEnabled && bool(s.interest, "dunning_steps.interest"),
        isReminder: bool(s.is_reminder, "dunning_steps.is_reminder"),
        ...(typeof s.label === "string" && s.label.trim() ? { label: s.label.trim() } : {}),
      };
    });
  const keys = new Set<string>();
  for (const s of stages) {
    if (keys.has(s.key)) throw new DomainError("SETTING_INVALID", `dunning_steps.key doppelt: ${s.key}`);
    keys.add(s.key);
  }

  const rateRows = rows.dunning_base_rates && rows.dunning_base_rates.length > 0 ? rows.dunning_base_rates : defaults.dunning_base_rates;
  const baseRates = Array.isArray(rateRows)
    ? (rateRows as DunningBaseRateRow[]).map((r) => {
        const validFrom = str(r.valid_from, "dunning_base_rates.valid_from").slice(0, 10);
        if (!isIsoDate(validFrom)) invalid("dunning_base_rates.valid_from", r.valid_from);
        return { validFrom, percent: bpToPercent(int(r.rate_bp, "dunning_base_rates.rate_bp")) };
      })
    : [];
  // PRAXIS-1: ein fehlender Basiszins (etwa Österreich, bevor der Betrieb ihn pflegt) legt nicht das Laden aller Einstellungen
  // lahm; computeDunningCosts lässt die Zinsen der betroffenen Mahnung mit Hinweis weg, die Mahnleiter sperrt das Einschalten

  const dunning: DunningPolicy = {
    stages,
    feeCurrency,
    feeMode,
    baseRates,
    surchargePercentPoints,
    flatFeeCents,
    // 0161: ohne Angabe an (Creator-Forderungen tragen Pauschale und Zinsen)
    creatorClaimsCosts: pol.creator_claims_costs !== false,
    // CH-2 (0271): vertraglicher Satz und Zinsbeginn (Vorgabe des Landes: CH/LI ab Mahnung, sonst ab Fälligkeit)
    contractRatePercent: pol.interest_rate_mode === "contract" && pol.contract_rate_bp != null ? bpToPercent(int(pol.contract_rate_bp, "contract_rate_bp")) : null,
    interestStart: pol.interest_start === "due" || pol.interest_start === "reminder" ? pol.interest_start
      : ["CH", "LI"].includes(String((rows.org_settings as Record<string, unknown> | null)?.country ?? "").toUpperCase()) ? "reminder" : "due",
  };

  return { settings, dunning };
}
